import { useEffect, useMemo, useCallback } from "react";
import { useFormik, FormikValues } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";

interface UseSessionStorageFiltersOptions<T extends FormikValues> {
  readonly storageKey: string;
  readonly defaultValues: T;
  readonly onFiltersChange?: (filters: T) => void;
}

interface UseSessionStorageFiltersReturn<T extends FormikValues> {
  readonly formik: ReturnType<typeof useFormik<T>>;
  readonly handleSearch: (value: string) => void;
  readonly handleApplyFilters: (values: Partial<T>) => void;
  readonly handleResetFilters: () => void;
  readonly navigateWithParams: (patch: Record<string, string | null | undefined>) => void;
}

/**
 * Универсальный хук для работы с фильтрами через sessionStorage
 * @param storageKey - ключ для хранения в sessionStorage
 * @param defaultValues - значения фильтров по умолчанию
 * @param onFiltersChange - коллбэк, вызываемый при изменении фильтров
 */
export const useSessionStorageFilters = <T extends FormikValues & { search?: string }>({
  storageKey,
  defaultValues,
  onFiltersChange,
}: UseSessionStorageFiltersOptions<T>): UseSessionStorageFiltersReturn<T> => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const limit = Number(searchParams.get("limit")) || 20;

  // Загрузка фильтров из sessionStorage
  const loadFiltersFromStorage = useCallback((): T => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultValues, ...parsed };
      }
    } catch (error) {
      console.error(`Error loading filters from sessionStorage (${storageKey}):`, error);
    }
    return defaultValues;
  }, [storageKey, defaultValues]);

  // Сохранение фильтров в sessionStorage
  const saveFiltersToStorage = useCallback(
    (filters: T): void => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(filters));
      } catch (error) {
        console.error(`Error saving filters to sessionStorage (${storageKey}):`, error);
      }
    },
    [storageKey],
  );

  // Инициализация фильтров из хранилища
  const initialFilters = useMemo((): T => {
    return loadFiltersFromStorage();
  }, [loadFiltersFromStorage]);

  // Formik для управления состоянием фильтров
  const formik = useFormik<T>({
    initialValues: initialFilters,
    enableReinitialize: false,
    onSubmit: () => {},
  });

  // Навигация с параметрами (только page и limit)
  const navigateWithParams = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      const params = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });
      navigate(`?${params.toString()}`);
    },
    [navigate, searchParams],
  );

  // Обработчик поиска
  const handleSearch = useCallback(
    (value: string) => {
      formik.setFieldValue("search", value || "");
      navigateWithParams({ page: "1", limit: String(limit) });
    },
    [formik, navigateWithParams, limit],
  );

  // Применение фильтров
  const handleApplyFilters = useCallback(
    (values: Partial<T>) => {
      const updatedFilters = { ...formik.values, ...values };
      formik.setValues(updatedFilters);
      navigateWithParams({
        page: "1",
        limit: String(limit),
      });
    },
    [formik, navigateWithParams, limit],
  );

  // Сброс фильтров
  const handleResetFilters = useCallback(() => {
    formik.resetForm({ values: defaultValues });
    navigateWithParams({
      page: "1",
      limit: String(limit),
    });
  }, [formik, defaultValues, navigateWithParams, limit]);

  // Автоматическое сохранение фильтров при их изменении
  useEffect(() => {
    saveFiltersToStorage(formik.values);
    onFiltersChange?.(formik.values);
  }, [formik.values, saveFiltersToStorage, onFiltersChange]);

  return {
    formik,
    handleSearch,
    handleApplyFilters,
    handleResetFilters,
    navigateWithParams,
  };
};
